import { usePostHog } from '@posthog/react';
import { useCallback, useEffect, useState } from 'react';
import { capture } from '../analytics/posthog';
import ApiClient from '../config/ApiClient';
import type {
  SchedulePreferenceInput,
  SessionScheduleOption,
  SessionSchedulerPayload,
} from '../types/sessionSchedule';
import {
  type MyPreference,
  sameSlot,
  toPreferenceInputs,
} from '../utils/sessionScheduleClient';

type ScheduleSource =
  | { kind: 'assignment'; assignmentId: string }
  | { kind: 'lessonPlan'; lessonPlanId: string };

export function useSessionSchedule(source: ScheduleSource | undefined) {
  const posthog = usePostHog();
  const [data, setData] = useState<SessionSchedulerPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [updatingSelection, setUpdatingSelection] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [pollSessionNumber, setPollSessionNumber] = useState(1);

  const assignmentId = data?.assignment_id;
  const sessionNumber = pollSessionNumber;
  const currentProgramSessionNumber = data?.current_session_number;
  /** Hub session tied to the loaded payload; avoids POSTing option ids from another session during a switch. */
  const loadedPollSessionNumber = data?.poll_session_number ?? null;

  const scheduleBasePath = useCallback(() => {
    if (!assignmentId || loadedPollSessionNumber == null || loading) {
      return null;
    }
    return `/api/payment-assignments/${assignmentId}/sessions/${loadedPollSessionNumber}/schedule`;
  }, [assignmentId, loadedPollSessionNumber, loading]);

  const fetchSession = useCallback(
    async (url: string) => {
      setLoading(true);
      setError(null);
      const response = await ApiClient.get<SessionSchedulerPayload>(url);
      if (response.success && response.data) {
        setData(response.data);
        const fromPath = url.match(/\/sessions\/(\d+)\/schedule/);
        if (fromPath) {
          setPollSessionNumber(Number(fromPath[1]));
        } else if (response.data.poll_session_number != null) {
          setPollSessionNumber(response.data.poll_session_number);
        } else {
          setPollSessionNumber(response.data.current_session_number);
        }
      } else {
        setError(response.error || 'Failed to load schedule');
      }
      setLoading(false);
    },
    [],
  );

  const refresh = useCallback(async () => {
    if (!source) {
      return;
    }
    const url =
      source.kind === 'assignment'
        ? `/api/payment-assignments/${source.assignmentId}/schedule/`
        : `/api/lesson-plan/${source.lessonPlanId}/schedule/`;
    await fetchSession(url);
  }, [fetchSession, source]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const selectSession = useCallback(
    async (nextSessionNumber: number) => {
      const resolvedAssignmentId =
        assignmentId
        ?? (source?.kind === 'assignment' ? Number(source.assignmentId) : undefined);
      if (!resolvedAssignmentId) {
        return;
      }
      setPollSessionNumber(nextSessionNumber);
      await fetchSession(
        `/api/payment-assignments/${resolvedAssignmentId}/sessions/${nextSessionNumber}/schedule/`,
      );
    },
    [assignmentId, fetchSession, source],
  );

  const savePreferences = useCallback(
    async (
      preferences: SchedulePreferenceInput[],
    ): Promise<{ success: true } | { success: false; error: string }> => {
      const base = scheduleBasePath();
      if (!base) {
        const message = 'Schedule is not ready to save yet';
        setError(message);
        return { success: false, error: message };
      }
      setSavingPreferences(true);
      const response = await ApiClient.put<SessionSchedulerPayload>(
        `${base}/preferences/`,
        preferences,
      );
      setSavingPreferences(false);
      if (response.success && response.data) {
        setData(response.data);
        setError(null);
        capture(posthog, 'session_schedule_preferences_saved', {
          assignment_id: response.data.assignment_id,
          session_number: pollSessionNumber,
        });
        return { success: true };
      }
      const message = response.error || 'Failed to save preferences';
      setError(message);
      return { success: false, error: message };
    },
    [pollSessionNumber, posthog, scheduleBasePath],
  );

  const toggleAvailability = useCallback(
    async (option: SessionScheduleOption, selected: boolean, current: MyPreference[]) => {
      const base = scheduleBasePath();
      if (!base) {
        return false;
      }
      const remaining = current.filter((pref) => !sameSlot(pref, option));
      const nextSet = selected
        ? [
          ...remaining,
          {
            starts_at: option.starts_at,
            duration_minutes: option.duration_minutes,
            repeats: null,
          },
        ]
        : remaining;
      if (nextSet.length === 0) {
        return false;
      }
      setUpdatingSelection(true);
      try {
        const response = await ApiClient.put<SessionSchedulerPayload>(
          `${base}/preferences/`,
          toPreferenceInputs(nextSet),
        );
        if (response.success && response.data) {
          setData(response.data);
          capture(posthog, 'session_schedule_availability_toggled', {
            option_id: option.option_id,
            selected,
            assignment_id: response.data.assignment_id,
            session_number: pollSessionNumber,
          });
          return true;
        }
        setError(response.error || 'Failed to update availability');
        return false;
      } finally {
        setUpdatingSelection(false);
      }
    },
    [pollSessionNumber, posthog, scheduleBasePath],
  );

  const confirmSchedule = useCallback(
    async (
      optionId: number,
    ): Promise<{ success: true } | { success: false; error: string }> => {
      const base = scheduleBasePath();
      if (!base) {
        const message = 'Schedule is not ready to confirm yet';
        setError(message);
        return { success: false, error: message };
      }
      setConfirming(true);
      const response = await ApiClient.post<SessionSchedulerPayload>(`${base}/confirm/`, {
        option_id: optionId,
      });
      setConfirming(false);
      if (response.success && response.data) {
        setData(response.data);
        setError(null);
        capture(posthog, 'session_schedule_confirmed', {
          option_id: optionId,
          assignment_id: response.data.assignment_id,
          session_number: pollSessionNumber,
        });
        return { success: true };
      }
      const conflictPayload = response.errors as SessionSchedulerPayload | undefined;
      if (conflictPayload?.status === 'confirmed') {
        setData(conflictPayload);
        setError(null);
        return { success: true };
      }
      const message = response.error || 'Failed to confirm';
      setError(message);
      return { success: false, error: message };
    },
    [pollSessionNumber, posthog, scheduleBasePath],
  );

  const declineConfirmedSchedule = useCallback(
    async (): Promise<{ success: true } | { success: false; error: string }> => {
      const base = scheduleBasePath();
      if (!base) {
        const message = 'Schedule is not ready yet';
        setError(message);
        return { success: false, error: message };
      }
      setDeclining(true);
      const response = await ApiClient.post<SessionSchedulerPayload>(`${base}/decline/`, {});
      setDeclining(false);
      if (response.success && response.data) {
        setData(response.data);
        setError(null);
        capture(posthog, 'session_schedule_declined', {
          assignment_id: response.data.assignment_id,
          session_number: pollSessionNumber,
        });
        return { success: true };
      }
      const message = response.error || 'Failed to update schedule';
      setError(message);
      return { success: false, error: message };
    },
    [pollSessionNumber, posthog, scheduleBasePath],
  );

  return {
    data,
    loading,
    error,
    refresh,
    selectSession,
    savePreferences,
    savingPreferences,
    updatingSelection,
    toggleAvailability,
    confirmSchedule,
    confirming,
    declineConfirmedSchedule,
    declining,
    assignmentId,
    sessionNumber,
    currentProgramSessionNumber,
  };
}
